import { useEffect, useId, useState } from 'react';
import { t } from '@app/i18n';
import { useCanOperar } from '@app/auth/useCan';
import { Alert, Button, Card, ErrorBanner, InfoTip, LoadingButton } from '@zarya/ui';
import type { Environment } from '../TenantSettings/TenantSettingsModel';
import {
  useEnvironmentApprovalRules,
  useUpdateEnvironmentApprovalRules,
} from './ApprovalWorkflow.api';
import {
  APPROVAL_CLASSES,
  DEPLOYMENT_MODES,
  deploymentOrDefault,
  effectiveRules,
  applyApprovalMode,
  type ClassMemory,
  type ApprovalRule,
  type ApprovalMode,
  type DeploymentMode,
} from './ApprovalWorkflowModel';
import s from './ApprovalWorkflow.module.css';

export function ApprovalWorkflowCard({ environment, onStateChange }: {
  environment: Environment;
  onStateChange?: (state: { dirty: boolean; pending: boolean }) => void;
}) {
  const query = useEnvironmentApprovalRules(environment.id);
  const save = useUpdateEnvironmentApprovalRules(environment.id);
  const canEdit = useCanOperar('updateEnvironmentApprovalRules');
  const id = useId();
  const [draft, setDraft] = useState<ApprovalRule[] | null>(null);
  // As classes que cada evento tinha ao sair de `granular` (§8.4). Vive ao lado do
  // rascunho e morre com ele: recarregar a pagina volta ao que esta gravado, e e o
  // aviso abaixo que impede essa segunda visita de enganar.
  const [memory, setMemory] = useState<ClassMemory>({});
  // P28 — o rascunho da linha «Deployment após aprovação». Estado próprio, e não uma quarta
  // linha em `draft`: ela não é uma regra de aprovação (não tem evento nem classes), e
  // enfiá-la na mesma lista a faria passar por `effectiveRules`, que repõe o que falta como
  // `always` — um valor que não existe neste vocabulário.
  const [deploymentDraft, setDeploymentDraft] = useState<DeploymentMode | null>(null);
  const persisted = effectiveRules(query.data?.rules ?? []);
  const rules = draft ?? persisted;
  const persistedDeployment = deploymentOrDefault(query.data?.deployment_after_approval);
  const deployment = deploymentDraft ?? persistedDeployment;
  const dirty =
    (draft !== null && JSON.stringify(draft) !== JSON.stringify(persisted)) ||
    deployment !== persistedDeployment;

  useEffect(() => {
    onStateChange?.({ dirty, pending: save.isPending });
  }, [dirty, save.isPending, onStateChange]);

  function edit(next: ApprovalRule[]) {
    save.reset();
    setDraft(next);
  }
  function modeAt(index: number, mode: ApprovalMode) {
    const { rules: proximas, memory: proxima } = applyApprovalMode(rules, index, mode, memory);
    setMemory(proxima);
    edit(proximas);
  }

  return (
    <Card title={environment.name} subtitle={t('approvalWorkflow.card.subtitle')}>
      {query.isLoading ? <p>{t('approvalWorkflow.loading')}</p> : null}
      {query.isError ? (
        <ErrorBanner
          error={query.error}
          title={t('approvalWorkflow.error.read')}
          onRetry={() => void query.refetch()}
          hideLink
        />
      ) : null}
      {query.data ? (
        <form
          aria-label={t('approvalWorkflow.form.label', { name: environment.name })}
          onSubmit={(event) => {
            event.preventDefault();
            if (!canEdit || !dirty || save.isPending) return;
            save.mutate(
              { rules: effectiveRules(rules), deployment_after_approval: deployment },
              {
                onSuccess: () => {
                  setDraft(null);
                  setDeploymentDraft(null);
                },
              },
            );
          }}
        >
          <p className={s.intro}>{t('approvalWorkflow.actions')}</p>
          {!environment.selectable_for_identity ? (
            <Alert tone="info" title={t('approvalWorkflow.notSelectable')} />
          ) : null}
          {!canEdit ? <p className={s.help}>{t('approvalWorkflow.readOnly')}</p> : null}
          {rules.map((rule, index) => (
            <fieldset className={s.event} key={rule.event} disabled={!canEdit || save.isPending}>
              <legend>{t(`approvalWorkflow.event.${rule.event}`)}</legend>
              <div className={s.choices}>
                {(
                  [
                    'always',
                    'never',
                    ...(rule.event === 'agent_creation' ? [] : ['granular']),
                  ] as ApprovalMode[]
                ).map((mode) => (
                  <label key={mode} className={s.choice}>
                    <input
                      type="radio"
                      name={`${id}-${rule.event}`}
                      checked={rule.mode === mode}
                      onChange={() => modeAt(index, mode)}
                    />
                    {t(`approvalWorkflow.mode.${mode}`)}
                  </label>
                ))}
              </div>
              {rule.mode === 'granular' ? (
                <div className={s.classes}>
                  {APPROVAL_CLASSES.map((value) => (
                    <label key={value} className={s.choice}>
                      <input
                        type="checkbox"
                        checked={rule.classes.includes(value)}
                        onChange={(event) => {
                          const classes = APPROVAL_CLASSES.filter((candidate) =>
                            candidate === value
                              ? event.target.checked
                              : rule.classes.includes(candidate),
                          );
                          edit(
                            rules.map((current, i) =>
                              i === index ? { ...current, classes } : current,
                            ),
                          );
                        }}
                      />
                      {value}
                    </label>
                  ))}
                  <p className={s.help}>{t('approvalWorkflow.classes.help')}</p>
                  <p className={s.help}>{t('approvalWorkflow.public.help')}</p>
                  {/* §8.5: `granular` sem classe é estado VÁLIDO — a migração (§4.6)
                      produz exatamente isso para quem vinha de manual/assistido —, e
                      por isso descreve-se em vez de se recusar: não bloqueia o Salvar
                      e não é `role="alert"`. O texto diz «tudo o que o produto
                      conseguir classificar», e não «nada exige pessoa», porque a
                      criticidade desconhecida continua a exigir (linha 6, §4.4). */}
                  {rule.classes.length === 0 ? (
                    <p className={s.help} data-testid={`empty-warning-${rule.event}`}>
                      {t('approvalWorkflow.classes.emptyWarning')}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </fieldset>
          ))}
          {/* P28 — «Deployment após aprovação». Fica ABAIXO das três linhas de evento e
              dentro do mesmo formulário porque é o mesmo Salvar: as regras dizem quem
              aprova, esta linha diz quando o aprovado sobe. O InfoTip é obrigatório em campo
              de configuração (FR-132) e carrega o texto do mrolim. */}
          <fieldset className={s.event} disabled={!canEdit || save.isPending}>
            <legend>
              {t('approvalWorkflow.deployment.legend')}{' '}
              <InfoTip text={t('approvalWorkflow.deployment.help')} />
            </legend>
            <div className={s.choices}>
              {DEPLOYMENT_MODES.map((mode) => (
                <label key={mode} className={s.choice}>
                  <input
                    type="radio"
                    name={`${id}-deployment`}
                    checked={deployment === mode}
                    onChange={() => {
                      save.reset();
                      setDeploymentDraft(mode);
                    }}
                  />
                  {t(`approvalWorkflow.deployment.${mode}`)}
                </label>
              ))}
            </div>
          </fieldset>
          {save.isError ? (
            <ErrorBanner
              error={save.error}
              title={t('approvalWorkflow.error.save')}
              escrita
              hideLink
            />
          ) : null}
          {save.isSuccess ? <Alert tone="info" title={t('approvalWorkflow.saved')} /> : null}
          {canEdit ? (
            <div className={s.actions}>
              <Button
                variant="out"
                disabled={save.isPending}
                onClick={() => edit(effectiveRules([]))}
              >
                {t('approvalWorkflow.requireAll')}
              </Button>
              <div className={s.saveActions}>
                <Button
                  variant="soft"
                  disabled={save.isPending || !dirty}
                  onClick={() => {
                    save.reset();
                    setDraft(null);
                    setDeploymentDraft(null);
                  }}
                >
                  {t('approvalWorkflow.cancel')}
                </Button>
                <LoadingButton
                  type="submit"
                  loading={save.isPending}
                  loadingLabel={t('approvalWorkflow.saving')}
                  disabled={!dirty}
                >
                  {t('approvalWorkflow.save')}
                </LoadingButton>
              </div>
            </div>
          ) : null}
        </form>
      ) : null}
    </Card>
  );
}
